import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Space, Typography, message, Table, DatePicker, Tabs } from 'antd';
import { DownloadOutlined, FunnelPlotOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { fetchVacancyReport, exportVacancyReportToExcel } from '../../redux/analyticsSlice';
import moment from 'moment';
import 'moment/locale/ru';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ChartTitle,
  Tooltip,
  Legend
);

const { Text } = Typography;
const { RangePicker } = DatePicker;

const FAILURE_REASONS = {
  no_show: 'Не пришел на собеседование',
  irrelevant_experience: 'Нерелевантный опыт работы',
  irrelevant_salary: 'Нерелевантный доход',
  no_contact: 'Не выходит на связь',
  not_interested_product: 'Не интересен товар',
  unsuitable_schedule: 'Не подходит график работы',
  not_actively_searching: 'Не актуален поиск работы',
  not_interested_functionality: 'Не интересен функционал',
  long_commute: 'Далеко добираться',
  failed_test_assignment: 'Не прошел тестовое задание',
  vacancy_closed: 'Вакансия закрыта',
  other_requirements: 'Не подходит по другим требованиям'
};

const VacancyReportTab = ({ vacancy, organisations }) => {
  const dispatch = useDispatch();
  const { isLoading: reportLoading, isExporting } = useSelector((state) => state.analytics);
  const [dateRange, setDateRange] = useState(null);
  const [reportData, setReportData] = useState(null);

  const handleGenerateReport = async () => {
    if (!vacancy?.id) return;

    try {
      const asOfDate = dateRange ? dateRange[1].format('YYYY-MM-DD') : null;

      const apiReport = await dispatch(fetchVacancyReport({
        vacancyId: vacancy.id,
        asOfDate
      })).unwrap();

      const vacancyOrg = organisations.find(o =>
        (o.id === vacancy?.organisation_id || o.organisation_id === vacancy?.organisation_id)
      );

      const report = {
        ...apiReport,
        vacancy: vacancy,
        generatedAt: moment().format('DD.MM.YYYY HH:mm'),
        organisation: vacancyOrg?.name || apiReport.organisation_name || 'Не указана',
        dateRange: dateRange ? {
          start: dateRange[0].format('DD.MM.YYYY'),
          end: dateRange[1].format('DD.MM.YYYY')
        } : null
      };

      setReportData(report);
    } catch (error) {
      message.error('Ошибка при генерации отчета');
      console.error(error);
    }
  };

  // Автоматически загружаем отчет при открытии таба
  useEffect(() => {
    if (vacancy?.id) {
      handleGenerateReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vacancy?.id]);

  const handleExportToExcel = async () => {
    try {
      await dispatch(exportVacancyReportToExcel(vacancy.id)).unwrap();
      message.success('Отчет успешно экспортирован');
    } catch (error) {
      message.error('Ошибка при экспорте отчета');
      console.error(error);
    }
  };

  const funnelColumns = [
    {
      title: 'Этап',
      dataIndex: 'stage',
      key: 'stage',
    },
    {
      title: 'Количество',
      dataIndex: 'count',
      key: 'count',
      align: 'center',
    },
  ];

  const failuresColumns = [
    {
      title: 'Причина отказа',
      dataIndex: 'failure_reason',
      key: 'failure_reason',
    },
    {
      title: 'Количество',
      dataIndex: 'count',
      key: 'count',
    },
    {
      title: 'Доля, %',
      dataIndex: 'share',
      key: 'share',
    }
  ];

  const funnelAllTimeData = useMemo(() => {
    if (!reportData?.funnel_all_time) return [];

    return Object.entries(reportData.funnel_all_time).map(([stage, count], index) => ({
      key: `all-${index}`,
      stage,
      count,
    }));
  }, [reportData]);

  const funnelCurrentData = useMemo(() => {
    if (!reportData?.funnel_current) return [];

    return Object.entries(reportData.funnel_current).map(([stage, count], index) => ({
      key: `current-${index}`,
      stage,
      count: count || 0,
    }));
  }, [reportData]);

  const failuresData = useMemo(() => {
    if (!reportData?.failures || reportData.failures.length === 0) return [];

    const failureReasonsArray = Object.keys(FAILURE_REASONS).map(key => ({
      key: key,
      title: FAILURE_REASONS[key]
    }));

    return failureReasonsArray.map(reason => ({
      key: `failure-${reason.key}`,
      count: reportData.failures.find(failure => failure.reason === reason.key)?.count || 0,
      share: reportData.failures.find(failure => failure.reason === reason.key)?.share || 0,
      failure_reason: reason.title,
    }));
  }, [reportData]);

  const chartDataAllTime = useMemo(() => {
    if (!funnelAllTimeData || funnelAllTimeData.length === 0) return null;

    return {
      labels: funnelAllTimeData.map(item => item.stage),
      datasets: [
        {
          label: 'Количество кандидатов',
          data: funnelAllTimeData.map(item => item.count),
          backgroundColor: 'rgba(54, 162, 235, 0.6)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [funnelAllTimeData]);

  const chartDataCurrent = useMemo(() => {
    if (!funnelCurrentData || funnelCurrentData.length === 0) return null;

    return {
      labels: funnelCurrentData.map(item => item.stage),
      datasets: [
        {
          label: 'Количество кандидатов',
          data: funnelCurrentData.map(item => item.count),
          backgroundColor: 'rgba(75, 192, 192, 0.6)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [funnelCurrentData]);

  const chartDataFailures = useMemo(() => {
    if (!failuresData || failuresData.length === 0) return null;

    return {
      labels: failuresData.map(item => item.failure_reason),
      datasets: [
        {
          label: 'Количество',
          data: failuresData.map(item => item.count),
          backgroundColor: 'rgba(255, 99, 132, 0.6)',
          borderColor: 'rgba(255, 99, 132, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [failuresData]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  const chartOptionsHorizontal = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Фильтры */}
      <div>
        <Text strong>Период отчета (необязательно):</Text>
        <Space style={{ width: '100%', marginTop: '8px' }}>
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            format="DD.MM.YYYY"
            placeholder={['Дата начала', 'Дата окончания']}
          />
          <Button
            type="primary"
            onClick={handleGenerateReport}
            loading={reportLoading}
          >
            Обновить отчет
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportToExcel}
            loading={isExporting}
          >
            Экспорт в Excel
          </Button>
        </Space>
      </div>

      {/* Результат отчета */}
      {reportData && (
        <>
          <Text type="secondary">
            Дата формирования: {reportData.generatedAt}
            {reportData.dateRange && ` | Период: ${reportData.dateRange.start} - ${reportData.dateRange.end}`}
          </Text>

          <Tabs
            defaultActiveKey="funnel_all"
            items={[
              {
                key: 'funnel_all',
                label: (
                  <span>
                    <FunnelPlotOutlined /> Воронка за все время
                  </span>
                ),
                children: (
                  <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <Table
                      columns={funnelColumns}
                      dataSource={funnelAllTimeData}
                      pagination={false}
                      size="small"
                      bordered
                    />
                    {chartDataAllTime && (
                      <div style={{ height: '300px', marginTop: '16px' }}>
                        <Bar data={chartDataAllTime} options={chartOptions} />
                      </div>
                    )}
                  </Space>
                ),
              },
              {
                key: 'funnel_current',
                label: (
                  <span>
                    <FunnelPlotOutlined /> Текущая воронка
                  </span>
                ),
                children: (
                  <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <Table
                      columns={funnelColumns}
                      dataSource={funnelCurrentData}
                      pagination={false}
                      size="small"
                      bordered
                    />
                    {chartDataCurrent && (
                      <div style={{ height: '300px', marginTop: '16px' }}>
                        <Bar data={chartDataCurrent} options={chartOptions} />
                      </div>
                    )}
                  </Space>
                ),
              },
              {
                key: 'failures',
                label: (
                  <span>
                    <CloseCircleOutlined /> Отказы ({failuresData.length})
                  </span>
                ),
                children: (
                  <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <Table
                      columns={failuresColumns}
                      dataSource={failuresData}
                      pagination={failuresData.length > 20 ? { pageSize: 20 } : false}
                      size="small"
                      bordered
                      locale={{ emptyText: 'Нет отказов' }}
                    />
                    {chartDataFailures && (
                      <div style={{ height: '400px', marginTop: '16px' }}>
                        <Bar data={chartDataFailures} options={chartOptionsHorizontal} />
                      </div>
                    )}
                  </Space>
                ),
              },
            ]}
          />
        </>
      )}
    </Space>
  );
};

export default VacancyReportTab;

